import type { MobileDateTimePickerProps } from '@mui/x-date-pickers/MobileDateTimePicker';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';

export type PickerView = NonNullable<MobileDateTimePickerProps['view']>;

type FieldKey = Pick<
  KeyboardEvent,
  'key' | 'preventDefault' | 'stopPropagation'
>;

export type DateTimeDialog = {
  open: boolean;
  view: PickerView;
  close: () => void;
  changeView: (view: PickerView) => void;
  moveToClockAfterDay: () => void;
  fieldHandlers: {
    onClick?: () => void;
    onKeyDown?: (event: FieldKey) => void;
  };
};

const OPEN_VIEW: PickerView = 'day';

/**
 * v8 mobile pickers open from an icon button, take typing, and wait for
 * "Next" between the date and the time. This keeps the v7 dialog: a field
 * that opens on click, Space or Enter, and a day pick that moves on to the
 * clock.
 */
export function useDateTimeDialog(canOpen: boolean): DateTimeDialog {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PickerView>(OPEN_VIEW);
  const openPicker = (): void => {
    setView(OPEN_VIEW);
    setOpen(true);
  };
  const openOnSpaceOrEnter = (event: FieldKey): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    openPicker();
    event.preventDefault();
    event.stopPropagation();
  };
  return {
    open,
    view,
    close: () => setOpen(false),
    changeView: setView,
    moveToClockAfterDay: () => {
      if (view === OPEN_VIEW) setView('hours');
    },
    fieldHandlers: {
      onClick: canOpen ? openPicker : undefined,
      onKeyDown: canOpen ? openOnSpaceOrEnter : undefined,
    },
  };
}
